import React, { useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function RMSChart({
  data = [],
  range = 'Live',
  limits = { x: 0, y: 0, z: 0 },
  showLimits = true,
}) {
  const chartRef = useRef(null);
  const labels = data.map((d) => d.inserted_at);

  const limitLabelPlugin = {
    id: 'limitLabel',
    afterDatasetsDraw: (chart) => {
      if (!showLimits) return;
      const { ctx, chartArea } = chart;
      if (!chartArea) return;
      chart.data.datasets.forEach((ds, i) => {
        if (ds.label && ds.label.includes('Limit')) {
          const meta = chart.getDatasetMeta(i);
          if (!meta.data.length) return;
          const y = meta.data[0].y;
          ctx.save();
          ctx.fillStyle = ds.borderColor || 'orange';
          ctx.font = '12px "Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'bottom';
          ctx.fillText(ds.label, chartArea.left + 8, y - 6);
          ctx.restore();
        }
      });
    },
  };

  const formatTick = (label) => {
    const d = new Date(label);
    if (['1 Week', '1 Month', 'Custom'].includes(range)) {
      return d.toLocaleDateString();
    }
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const maxTicksByRange = {
    '12 Hours': 12,
    '24 Hours': 24,
    '1 Week': 7,
    '1 Month': 31,
  };

  const MAX_VISIBLE_TICKS = 10;
  const maxForRange = maxTicksByRange[range];
  const allowedTickCount = Math.max(
    1,
    Math.min(labels.length, maxForRange ? Math.min(maxForRange, MAX_VISIBLE_TICKS) : MAX_VISIBLE_TICKS)
  );
  const step = Math.ceil(labels.length / allowedTickCount) || 1;

  const chartData = {
    labels,
    datasets: [
      {
        label: 'RMS X',
        data: data.map((d) => d.rms_x),
        borderColor: '#ff0078',
        backgroundColor: (ctx) => {
          const { chart } = ctx;
          const { ctx: c, chartArea } = chart;
          if (!chartArea) return 'rgba(255,0,120,0.2)';
          const gradient = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(255,0,120,0.35)');
          gradient.addColorStop(1, 'rgba(255,0,120,0)');
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointStyle: 'circle',
      },
      {
        label: 'RMS Y',
        data: data.map((d) => d.rms_y),
        borderColor: '#00ff78',
        backgroundColor: (ctx) => {
          const { chart } = ctx;
          const { ctx: c, chartArea } = chart;
          if (!chartArea) return 'rgba(0,255,120,0.2)';
          const gradient = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(0,255,120,0.35)');
          gradient.addColorStop(1, 'rgba(0,255,120,0)');
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointStyle: 'circle',
      },
      {
        label: 'RMS Z',
        data: data.map((d) => d.rms_z),
        borderColor: '#0078ff',
        backgroundColor: (ctx) => {
          const { chart } = ctx;
          const { ctx: c, chartArea } = chart;
          if (!chartArea) return 'rgba(0,120,255,0.2)';
          const gradient = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(0,120,255,0.35)');
          gradient.addColorStop(1, 'rgba(0,120,255,0)');
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointStyle: 'circle',
      },
      ...(showLimits
        ? [
            {
              label: 'X Limit',
              data: labels.map(() => limits.x),
              borderColor: 'orange',
              borderDash: [4, 4],
              fill: false,
              pointRadius: 0,
              pointHitRadius: 10,
              tension: 0,
            },
            {
              label: 'Y Limit',
              data: labels.map(() => limits.y),
              borderColor: 'orange',
              borderDash: [4, 4],
              fill: false,
              pointRadius: 0,
              pointHitRadius: 10,
              tension: 0,
            },
            {
              label: 'Z Limit',
              data: labels.map(() => limits.z),
              borderColor: 'orange',
              borderDash: [4, 4],
              fill: false,
              pointRadius: 0,
              pointHitRadius: 10,
              tension: 0,
            },
          ]
        : []),
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: 'nearest' },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Time',
          color: '#cbd5f5',
        },
        ticks: {
          autoSkip: false,
          color: '#94a3b8',
          callback: (value, index) => (index % step === 0 ? formatTick(labels[index]) : ''),
          maxRotation: 0,
        },
        grid: {
          drawOnChartArea: false,
          drawTicks: false,
          display: false,
        },
        border: {
          display: false,
        },
      },
      y: {
        title: {
          display: true,
          text: 'RMS',
          color: '#cbd5f5',
        },
        ticks: {
          color: '#94a3b8',
        },
        grid: {
          color: 'rgba(148, 163, 184, 0.1)',
          drawBorder: false,
        },
        border: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true, color: '#e2e8f0', padding: 20 },
      },
      tooltip: {
        callbacks: {
          title: (items) => {
            const idx = items[0].dataIndex;
            return new Date(labels[idx]).toLocaleString();
          },
        },
      },
    },
  };

  return (
    <div className="h-full w-full">
      <Line
        ref={chartRef}
        data={chartData}
        options={options}
        plugins={[limitLabelPlugin]}
        style={{ backgroundColor: 'transparent' }}
      />
    </div>
  );
}

export default RMSChart;
